/**
 * Proposal card (docs/ux/project-tab-spec.md §1.3a): what the triage agent would do, confirmed
 * by the user. "Do it" signs exactly the shown actions (digest-bound); "Adjust…" edits them
 * inline first; "Keep talking" leaves it in the thread as not taken.
 */
import type { BarkProposal, ProposedOp } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { useState } from 'react'
import { describeOp } from '../../lib/discuss-store.js'

const STATUS_TEXT: Record<BarkProposal['status'], string> = {
  open: 'Proposed',
  confirmed: 'Done',
  adjusted: 'Done (adjusted)',
  declined: 'Not taken',
  superseded: 'Replaced',
  expired: 'Expired',
}

export interface ProposalCardProps {
  proposal: BarkProposal
  busy?: boolean
  onDo: () => void
  onAdjust: (actions: ProposedOp[]) => void
  onKeepTalking: () => void
}

export function ProposalCard({
  proposal: p,
  busy,
  onDo,
  onAdjust,
  onKeepTalking,
}: ProposalCardProps) {
  const [editing, setEditing] = useState<ProposedOp[] | null>(null)
  const open = p.status === 'open'
  const verbatim = p.actions.some((a) => a.op === 'task.create' || a.op === 'run.steer')
  return (
    <section
      aria-label={`Proposal: ${p.title}`}
      data-proposal={p.id}
      className={`grid gap-2 rounded-xl border p-3 ${
        open ? 'border-accent-text bg-surface-1' : 'border-border-subtle bg-surface-2'
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-[14px] font-semibold text-text-2">{STATUS_TEXT[p.status]}</span>
        <h3 className="m-0 min-w-0 flex-1 text-[15px] font-semibold text-text-1">{p.title}</h3>
      </div>
      {editing ? (
        <ul className="m-0 grid list-none gap-2 p-0">
          {editing.map((op, i) =>
            op.op === 'task.create' ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: ops have no id; order is the identity
              <li key={i}>
                <label className="grid gap-1 text-[14px] text-text-2">
                  Task title
                  <input
                    className="rounded-md border border-border-subtle bg-surface-1 px-2 py-1 text-[15px] text-text-1"
                    value={op.title}
                    onChange={(e) =>
                      setEditing(
                        editing.map((x, j) => (j === i ? { ...op, title: e.target.value } : x)),
                      )
                    }
                  />
                </label>
                <label className="mt-1 flex items-center gap-2 text-[14px] text-text-2">
                  <input
                    type="checkbox"
                    checked={!!op.autoLand}
                    onChange={(e) =>
                      setEditing(
                        editing.map((x, j) =>
                          j === i ? { ...op, autoLand: e.target.checked } : x,
                        ),
                      )
                    }
                  />
                  Auto-land on green
                </label>
              </li>
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: ops have no id; order is the identity
              <li key={i} className="flex items-center gap-2 text-[15px] text-text-1">
                <span className="flex-1">{describeOp(op)}</span>
                <button
                  type="button"
                  className="text-[14px] text-text-2"
                  onClick={() => setEditing(editing.filter((_x, j) => j !== i))}
                >
                  Remove
                </button>
              </li>
            ),
          )}
        </ul>
      ) : (
        <ul className="m-0 grid list-disc gap-1 pl-5 text-[15px] text-text-1">
          {p.actions.map((op, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: ops have no id; order is the identity
            <li key={i}>{describeOp(op)}</li>
          ))}
        </ul>
      )}
      {p.note ? <p className="m-0 text-[14px] text-text-2">{p.note}</p> : null}
      {verbatim ? (
        <p className="m-0 text-[14px] text-text-3">Your words go to the agent verbatim.</p>
      ) : null}
      {open ? (
        <div className="flex flex-wrap gap-2">
          {editing ? (
            <>
              <Button
                variant="primary"
                size="sm"
                disabled={
                  busy ||
                  !editing.length ||
                  editing.some((o) => o.op === 'task.create' && !o.title.trim())
                }
                onClick={() => {
                  onAdjust(editing)
                  setEditing(null)
                }}
              >
                Do it as edited
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                Back
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" size="sm" disabled={busy} onClick={onDo}>
                Do it
              </Button>
              <Button size="sm" disabled={busy} onClick={() => setEditing(p.actions)}>
                Adjust…
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={onKeepTalking}>
                Keep talking
              </Button>
            </>
          )}
        </div>
      ) : null}
    </section>
  )
}
